import { useMemo, type RefObject } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRight, Inbox, Search, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group'
import { Kbd, KbdGroup } from '@/components/ui/kbd'
import { Skeleton } from '@/components/ui/skeleton'
import type { Form } from '../types'
import { ICONS } from './icons'
import { iconFor } from './model'

export function isMacLike(): boolean {
    if (typeof navigator === 'undefined') return false
    return /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent)
}

interface HeroProps {
    title: string
    query: string
    onQueryChange: (q: string) => void
    onSubmit: () => void
    onArrowDown: () => void
    onOpenPalette: () => void
    inputRef: RefObject<HTMLInputElement | null>
    featured: Form[] | null
    onStart: (f: Form) => void
    starting: string | null
    waitingCount: number
}

export function HomeHero({
    title, query, onQueryChange, onSubmit, onArrowDown, onOpenPalette, inputRef,
    featured, onStart, starting, waitingCount,
}: HeroProps) {
    const mac = useMemo(isMacLike, [])

    return (
        <div className="relative flex flex-col gap-6 px-5 py-8 sm:px-8 sm:py-10 lg:py-12">
            <div className="flex flex-col gap-3">
                <h1 className="text-3xl leading-tight font-semibold tracking-tight text-balance sm:text-4xl">{title}</h1>
                <p className="max-w-xl text-base text-pretty text-muted-foreground">
                    Declare, disclose or assess in a few minutes. Start a request and we’ll send it to the right reviewers.
                </p>
            </div>

            <form
                role="search"
                className="flex w-full max-w-2xl flex-col gap-2"
                onSubmit={(e) => { e.preventDefault(); onSubmit() }}
            >
                <label htmlFor="home-search" className="sr-only">What do you need to submit?</label>
                <InputGroup className="h-13 rounded-xl bg-background shadow-sm has-[[data-slot=input-group-control]:focus-visible]:ring-4 dark:bg-input/30">
                    <InputGroupAddon className="pl-4">
                        <Search className="size-5" />
                    </InputGroupAddon>
                    <InputGroupInput
                        ref={inputRef}
                        id="home-search"
                        type="search"
                        autoComplete="off"
                        enterKeyHint="go"
                        placeholder="What do you need to submit?"
                        className="text-base [&::-webkit-search-cancel-button]:hidden"
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'ArrowDown') { e.preventDefault(); onArrowDown() }
                            else if (e.key === 'Escape' && query) { e.preventDefault(); onQueryChange('') }
                        }}
                        aria-describedby="home-search-hint"
                    />
                    <InputGroupAddon align="inline-end" className="pr-2">
                        {query ? (
                            <InputGroupButton size="icon-sm" aria-label="Clear search" onClick={() => { onQueryChange(''); inputRef.current?.focus() }}>
                                <X />
                            </InputGroupButton>
                        ) : (
                            <InputGroupButton
                                size="sm"
                                className="hidden sm:inline-flex"
                                aria-label="Open quick start (keyboard shortcut)"
                                onClick={onOpenPalette}
                            >
                                <KbdGroup>
                                    <Kbd>{mac ? '⌘' : 'Ctrl'}</Kbd>
                                    <Kbd>K</Kbd>
                                </KbdGroup>
                            </InputGroupButton>
                        )}
                    </InputGroupAddon>
                </InputGroup>
                <p id="home-search-hint" className="sr-only">
                    Type to filter the forms below. Press Enter to start the best match, or the down arrow to move to the results.
                </p>
            </form>

            <FeaturedRow featured={featured} onStart={onStart} starting={starting} />

            {waitingCount > 0 && (
                <Button asChild variant="outline" size="sm" className="w-fit lg:hidden">
                    <Link to="/inbox">
                        <Inbox data-icon="inline-start" />
                        {waitingCount} waiting on you
                        <ArrowRight data-icon="inline-end" />
                    </Link>
                </Button>
            )}
        </div>
    )
}

function FeaturedRow({ featured, onStart, starting }: { featured: Form[] | null; onStart: (f: Form) => void; starting: string | null }) {
    if (featured === null) {
        return (
            <div className="flex h-8 items-center gap-2" aria-hidden>
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-7 w-32 rounded-full" />
                <Skeleton className="h-7 w-28 rounded-full" />
            </div>
        )
    }
    if (featured.length === 0) return null
    return (
        <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Popular:</span>
            <ul className="flex flex-wrap gap-2" role="list" aria-label="Popular forms">
                {featured.map((f) => {
                    const Icon = ICONS[iconFor(f)]
                    return (
                        <li key={f.id}>
                            <Button
                                variant="secondary"
                                size="sm"
                                className="rounded-full"
                                disabled={starting !== null}
                                onClick={() => onStart(f)}
                                aria-label={`Start ${f.name}`}
                            >
                                <Icon data-icon="inline-start" />
                                {f.name}
                            </Button>
                        </li>
                    )
                })}
            </ul>
            <Badge variant="outline" className="sr-only">Starts immediately</Badge>
        </div>
    )
}
